// src/pages/AdminScrapingCredsTab.jsx
// Accès plateformes — gestion des identifiants de scraping (super_admin)

import React, { useEffect, useState, useCallback } from 'react';
import {
  Eye, EyeOff, Pencil, Trash2, Check, X,
  RefreshCw, Globe, ChevronDown, ChevronRight, Database, ShieldCheck, AlertCircle, ImageOff,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { scrapingCredsAPI } from '../services/api';
import { extractError } from '../context/AuthContext';

// ─── Config plateformes (label + couleur seulement, logo via URL) ──

const PLATFORMS = {
  piter:      { label: 'Piter',           color: '#0ea5e9' },
  fieldglass: { label: 'SAP Fieldglass',  color: '#1d4ed8' },
  prounity:   { label: 'ProUnity',        color: '#7c3aed' },
  apec:       { label: 'APEC',            color: '#059669' },
  linkedin:   { label: 'LinkedIn',        color: '#0077b5' },
  turnover:   { label: 'Turnover IT',     color: '#ea580c' },
};

const LOGOS_KEY = 'wibusiness_platform_logos';

function loadLogos() {
  try { return JSON.parse(localStorage.getItem(LOGOS_KEY) || '{}'); }
  catch { return {}; }
}
function saveLogos(logos) {
  localStorage.setItem(LOGOS_KEY, JSON.stringify(logos));
}

// ─── Icône / logo de plateforme ────────────────────────────────

// ─── Badge source ──────────────────────────────────────────────

function SourceBadge({ source }) {
  if (source === 'database') return (
    <span style={{ whiteSpace:'nowrap', display:'inline-flex', alignItems:'center', gap:4, padding:'2px 8px', borderRadius:4,
      fontSize:'0.72rem', fontWeight:600, background:'rgba(34,197,94,0.1)', color:'#16a34a',
      border:'1px solid rgba(34,197,94,0.2)' }}>
      <Database size={10}/> Base de données
    </span>
  );
  if (source === 'env_var') return (
    <span style={{ whiteSpace:'nowrap', display:'inline-flex', alignItems:'center', gap:4, padding:'2px 8px', borderRadius:4,
      fontSize:'0.72rem', fontWeight:600, background:'rgba(234,179,8,0.1)', color:'#b45309',
      border:'1px solid rgba(234,179,8,0.2)' }}>
      <ShieldCheck size={10}/> Variable d'env
    </span>
  );
  return (
    <span style={{ whiteSpace:'nowrap', display:'inline-flex', alignItems:'center', gap:4, padding:'2px 8px', borderRadius:4,
      fontSize:'0.72rem', fontWeight:600, background:'rgba(239,68,68,0.08)', color:'#dc2626',
      border:'1px solid rgba(239,68,68,0.18)' }}>
      <AlertCircle size={10}/> Non défini
    </span>
  );
}

// ─── Ligne de champ ────────────────────────────────────────────

function FieldRow({ platform, field, onUpdated }) {
  const [editing, setEditing]   = useState(false);
  const [value,   setValue]     = useState('');
  const [visible, setVisible]   = useState(false);
  const [saving,  setSaving]    = useState(false);
  const [deleting, setDeleting] = useState(false);

  const isSecret = ['password', 'mdp', 'token', 'secret'].some(k =>
    field.field_name.toLowerCase().includes(k)
  );

  const save = async () => {
    if (!value.trim()) { toast.error('Valeur requise'); return; }
    setSaving(true);
    try {
      await scrapingCredsAPI.update(platform, field.field_name, value.trim());
      toast.success(`${field.label} mis à jour`);
      setEditing(false);
      setValue('');
      setVisible(false);
      onUpdated();
    } catch (e) { toast.error(extractError(e)); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Supprimer "${field.label}" de la base ?\nLa valeur retombera sur la variable d'environnement.`)) return;
    setDeleting(true);
    try {
      await scrapingCredsAPI.reset(platform, field.field_name);
      toast.success(`${field.label} réinitialisé`);
      onUpdated();
    } catch (e) { toast.error(extractError(e)); }
    finally { setDeleting(false); }
  };

  const cancel = () => { setEditing(false); setValue(''); setVisible(false); };

  return (
    <tr style={{ borderBottom:'1px solid var(--border)' }}>
      {/* Nom du champ */}
      <td style={{ padding:'10px 14px', width:'22%' }}>
        <span style={{ fontSize:'0.83rem', fontWeight:600, color:'var(--text-primary)' }}>
          {field.label}
        </span>
      </td>

      {/* Source */}
      <td style={{ padding:'10px 14px', width:'18%' }}>
        <SourceBadge source={field.source} />
      </td>

      {/* Valeur / Input */}
      <td style={{ padding:'10px 14px' }}>
        {editing ? (
          <input
            type={isSecret && !visible ? 'password' : 'text'}
            value={value}
            onChange={e => setValue(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') cancel(); }}
            placeholder={`Nouvelle valeur…`}
            autoFocus
            style={{ width:'100%', padding:'6px 10px', borderRadius:6,
              border:'1.5px solid #3B82F6', background:'var(--bg-elevated)',
              color:'var(--text-primary)', fontSize:'0.83rem', boxSizing:'border-box',
              outline:'none' }}
          />
        ) : (
          <span style={{ fontSize:'0.83rem', color: field.has_value ? 'var(--text-secondary)' : 'var(--text-muted)',
            fontFamily: isSecret && !visible && field.has_value ? 'monospace' : 'inherit' }}>
            {field.has_value
              ? (isSecret && !visible ? '••••••••••' : (field.value_masked || '(défini)'))
              : '—'}
          </span>
        )}
      </td>

      {/* Actions */}
      <td style={{ padding:'10px 14px', textAlign:'right', whiteSpace:'nowrap', width:'1%' }}>
        <div style={{ display:'inline-flex', gap:4, alignItems:'center' }}>
          {editing ? (
            <>
              {isSecret && (
                <button onClick={() => setVisible(v => !v)}
                  style={{ ...BTN_GHOST }}>
                  {visible ? <EyeOff size={14}/> : <Eye size={14}/>}
                </button>
              )}
              <button onClick={save} disabled={saving}
                style={{ ...BTN_BASE, background:'#22c55e', color:'white' }}>
                <Check size={13}/> {saving ? '…' : 'OK'}
              </button>
              <button onClick={cancel}
                style={{ ...BTN_GHOST }}>
                <X size={14}/>
              </button>
            </>
          ) : (
            <>
              {field.has_value && isSecret && (
                <button onClick={() => setVisible(v => !v)} title={visible ? 'Masquer' : 'Afficher'}
                  style={{ ...BTN_GHOST }}>
                  {visible ? <EyeOff size={14}/> : <Eye size={14}/>}
                </button>
              )}
              <button onClick={() => setEditing(true)} title="Modifier"
                style={{ ...BTN_GHOST }}>
                <Pencil size={14}/>
              </button>
              {field.source === 'database' && (
                <button onClick={remove} disabled={deleting} title="Supprimer (retombe sur .env)"
                  style={{ ...BTN_GHOST, color:'#dc2626' }}>
                  <Trash2 size={14}/>
                </button>
              )}
            </>
          )}
        </div>
      </td>
    </tr>
  );
}

const BTN_BASE = {
  display:'inline-flex', alignItems:'center', gap:4,
  padding:'4px 10px', borderRadius:5, border:'none',
  cursor:'pointer', fontSize:'0.78rem', fontWeight:600,
};
const BTN_GHOST = {
  ...BTN_BASE,
  background:'var(--bg-elevated)', color:'var(--text-muted)',
  border:'1px solid var(--border)',
};

// ─── Carte plateforme ──────────────────────────────────────────

function PlatformCard({ platformData, onUpdated }) {
  const [open, setOpen]           = useState(false);
  const [editLogo, setEditLogo]   = useState(false);
  const [logoInput, setLogoInput] = useState('');
  const [logos, setLogos]         = useState(loadLogos);

  const info  = PLATFORMS[platformData.platform] || { label: platformData.platform, color: '#6b7280' };
  const color = info.color;

  const setCount   = platformData.fields.filter(f => f.has_value).length;
  const totalCount = platformData.fields.length;
  const dbCount    = platformData.fields.filter(f => f.source === 'database').length;
  const allSet     = setCount === totalCount;
  const noneSet    = setCount === 0;

  const openLogoEdit = (e) => {
    e.stopPropagation();
    setLogoInput(logos[platformData.platform] || '');
    setEditLogo(true);
  };

  const saveLogo = (e) => {
    e.stopPropagation();
    const next = { ...logos, [platformData.platform]: logoInput.trim() };
    if (!logoInput.trim()) delete next[platformData.platform];
    saveLogos(next);
    setLogos(next);
    setEditLogo(false);
  };

  const cancelLogo = (e) => {
    e.stopPropagation();
    setEditLogo(false);
  };

  const currentLogo = logos[platformData.platform];

  return (
    <div style={{ border:'1px solid var(--border)', borderRadius:10, overflow:'hidden', background:'var(--bg-card)', position:'relative' }}>
      {/* Header cliquable */}
      <div style={{ display:'flex', alignItems:'center', gap:14, padding:'13px 18px' }}>

        {/* Logo / icône par défaut */}
        <button
          onClick={openLogoEdit}
          title="Modifier le logo"
          style={{ width:36, height:36, borderRadius:8, display:'flex', alignItems:'center',
            justifyContent:'center', background:`${color}18`, flexShrink:0,
            border:`1px dashed ${color}44`, cursor:'pointer', padding:0 }}
        >
          {currentLogo
            ? <img src={currentLogo} alt={info.label} onError={() => {}} style={{ width:22, height:22, objectFit:'contain', borderRadius:3 }}/>
            : <Globe size={17} color={color}/>
          }
        </button>

        {/* Champ édition logo URL */}
        {editLogo && (
          <div style={{ position:'absolute', zIndex:10, background:'var(--bg-card)', border:'1px solid var(--border)',
            borderRadius:8, padding:12, boxShadow:'0 4px 20px rgba(0,0,0,0.15)',
            display:'flex', gap:8, alignItems:'center', minWidth:320 }}
            onClick={e => e.stopPropagation()}>
            <ImageOff size={14} color="var(--text-muted)" style={{ flexShrink:0 }}/>
            <input
              autoFocus
              value={logoInput}
              onChange={e => setLogoInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') saveLogo(e); if (e.key === 'Escape') cancelLogo(e); }}
              placeholder="https://exemple.com/logo.png"
              style={{ flex:1, padding:'5px 9px', borderRadius:5, border:'1px solid var(--border)',
                background:'var(--bg-elevated)', color:'var(--text-primary)', fontSize:'0.8rem', outline:'none' }}
            />
            <button onClick={saveLogo} style={{ ...BTN_BASE, background:'#22c55e', color:'white', padding:'4px 9px' }}>
              <Check size={12}/>
            </button>
            <button onClick={cancelLogo} style={{ ...BTN_GHOST, padding:'4px 7px' }}>
              <X size={12}/>
            </button>
          </div>
        )}

        {/* Nom + progress — cliquable pour ouvrir la table */}
        <button
          onClick={() => setOpen(o => !o)}
          style={{ flex:1, minWidth:0, background:'none', border:'none', cursor:'pointer', textAlign:'left', padding:0 }}
        >
          <div style={{ fontWeight:700, fontSize:'0.92rem', color:'var(--text-primary)' }}>{info.label}</div>
          <div style={{ display:'flex', alignItems:'center', gap:8, marginTop:4 }}>
            <div style={{ width:80, height:4, borderRadius:2, background:'var(--border)', overflow:'hidden' }}>
              <div style={{ height:'100%', borderRadius:2, transition:'width 0.3s',
                width:`${totalCount ? (setCount / totalCount) * 100 : 0}%`,
                background: allSet ? '#22c55e' : noneSet ? 'var(--border)' : '#f59e0b' }}/>
            </div>
            <span style={{ fontSize:'0.73rem', color:'var(--text-muted)' }}>
              {setCount}/{totalCount} défini{setCount > 1 ? 's' : ''}
              {dbCount > 0 && <span style={{ marginLeft:6, color:'#16a34a' }}>· {dbCount} en BDD</span>}
            </span>
          </div>
        </button>

        {/* Badge statut */}
        <div style={{ flexShrink:0 }}>
          {allSet
            ? <span style={BADGE_GREEN}>Complet</span>
            : noneSet
              ? <span style={BADGE_RED}>Non configuré</span>
              : <span style={BADGE_YELLOW}>Partiel</span>
          }
        </div>

        {/* Chevron */}
        <button onClick={() => setOpen(o => !o)}
          style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-muted)', flexShrink:0, padding:4 }}>
          {open ? <ChevronDown size={15}/> : <ChevronRight size={15}/>}
        </button>
      </div>

      {/* Table des champs */}
      {open && (
        <div style={{ borderTop:'1px solid var(--border)' }}>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead>
              <tr style={{ background:'var(--bg-elevated)' }}>
                <th style={{ ...TH }}>Champ</th>
                <th style={{ ...TH }}>Source</th>
                <th style={{ ...TH }}>Valeur</th>
                <th style={{ ...TH, textAlign:'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {platformData.fields.map(field => (
                <FieldRow
                  key={field.field_name}
                  platform={platformData.platform}
                  field={field}
                  onUpdated={onUpdated}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const TH = {
  padding:'8px 14px', textAlign:'left', fontSize:'0.73rem',
  fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase',
  letterSpacing:'0.03em',
};

const BADGE_GREEN  = { padding:'3px 9px', borderRadius:12, fontSize:'0.72rem', fontWeight:700,
  background:'rgba(34,197,94,0.1)', color:'#16a34a', border:'1px solid rgba(34,197,94,0.2)' };
const BADGE_YELLOW = { padding:'3px 9px', borderRadius:12, fontSize:'0.72rem', fontWeight:700,
  background:'rgba(234,179,8,0.1)', color:'#b45309', border:'1px solid rgba(234,179,8,0.2)' };
const BADGE_RED    = { padding:'3px 9px', borderRadius:12, fontSize:'0.72rem', fontWeight:700,
  background:'rgba(239,68,68,0.08)', color:'#dc2626', border:'1px solid rgba(239,68,68,0.18)' };

// ─── Composant principal ───────────────────────────────────────

export default function AdminScrapingCredsTab() {
  const [platforms, setPlatforms] = useState([]);
  const [loading,   setLoading]   = useState(true);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await scrapingCredsAPI.list();
      setPlatforms(res.data || []);
    } catch (e) { toast.error(extractError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const configured  = platforms.filter(p => p.fields.every(f => f.has_value)).length;
  const partial     = platforms.filter(p => p.fields.some(f => f.has_value) && !p.fields.every(f => f.has_value)).length;

  return (
    <div>
      {/* En-tête */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:20 }}>
        <div>
          <h3 style={{ margin:'0 0 4px', fontSize:'1rem' }}>Accès plateformes</h3>
          <p style={{ margin:0, fontSize:'0.8rem', color:'var(--text-muted)' }}>
            Les identifiants stockés en base ont la priorité sur les variables d'environnement.
            La suppression d'une entrée revert sur le fichier .env.
          </p>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          {!loading && (
            <div style={{ fontSize:'0.78rem', color:'var(--text-muted)', textAlign:'right' }}>
              <span style={{ color:'#16a34a', fontWeight:600 }}>{configured}</span>/{platforms.length} complets
              {partial > 0 && <span style={{ marginLeft:8, color:'#b45309', fontWeight:600 }}>{partial} partiels</span>}
            </div>
          )}
          <button onClick={fetch}
            style={{ display:'flex', alignItems:'center', gap:6, padding:'7px 13px', borderRadius:6,
              border:'1px solid var(--border)', background:'var(--bg-elevated)', cursor:'pointer',
              color:'var(--text-muted)', fontSize:'0.83rem', fontWeight:500 }}>
            <RefreshCw size={13}/> Actualiser
          </button>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign:'center', padding:48 }}><div className="spinner"/></div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
          {platforms.map(p => (
            <PlatformCard key={p.platform} platformData={p} onUpdated={fetch}/>
          ))}
        </div>
      )}
    </div>
  );
}
